import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { fcArrowDropdown } from '@seeqdev/icons/seeq';
import Badge from '../Badge';
import Icon from '../Icon';
import { DEFAULT_TOOL_TIP_DELAY } from '../Tooltip/Tooltip.constants';
import { overlayStyles } from '../styles/overlays';
import { colors } from '../theme/colors.stylex';
import { controlMetrics } from '../theme/controls.stylex';
import { getSvgIconPath } from '../utils/svg';
import type { QTipDataAttributes } from '../Tooltip/QTip.types';
import type { ToolbarButtonProps } from './ToolbarButton.types';

const styles = stylex.create({
  trigger: {
    marginLeft: { 'default': '1.5px', ':first-child': 0 },
    marginRight: { 'default': '1.5px', ':last-child': 0 },
    borderStyle: 'none',
    backgroundColor: 'transparent',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
  },
  content: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderRadius: controlMetrics.radius,
  },
  small: { paddingLeft: '0.25rem', paddingRight: '0.25rem', paddingTop: '1px', paddingBottom: '1px' },
  regular: { paddingLeft: '0.5rem', paddingRight: '0.5rem', paddingTop: '0.3125rem', paddingBottom: '0.3125rem' },
  inactive: {
    backgroundColor: { 'default': 'transparent', ':hover': colors.toolbarHover, ':active': colors.toolbarHover },
  },
  active: { backgroundColor: { 'default': colors.toolbarSelected, ':active': colors.toolbarSelectedPressed } },
  highlighted: { backgroundColor: colors.badgeNewBackground },
  highlightedSmall: { marginLeft: '7px', width: '25px', height: '25px', justifyContent: 'center' },
  highlightedRegular: { marginLeft: '0.5rem' },
  badgeSlot: { pointerEvents: 'none', position: 'absolute', top: 0, right: '-0.5rem', lineHeight: 1 },
  badge: {
    backgroundColor: colors.warning,
    paddingLeft: '3px',
    paddingRight: '3px',
    paddingTop: '1px',
    paddingBottom: '1px',
    fontSize: '10px',
    color: colors.black,
  },
  disabled: { cursor: 'not-allowed', opacity: 0.5 },
  icons: { whiteSpace: 'nowrap' },
  icon: { color: colors.text },
  arrowIcon: { marginLeft: '0.1875rem' },
  label: { fontSize: '0.625rem', color: colors.text },
  popover: {
    position: 'relative',
    zIndex: 1000,
    minWidth: '1.5rem',
    padding: '0.5rem',
    borderRadius: controlMetrics.radius,
  },
});

const PopoverContent = forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Content ref={ref} align={align} sideOffset={sideOffset} {...props} asChild />
));
PopoverContent.displayName = 'PopoverContent';

export const ToolbarButton = ({
  isSmall = false,
  label,
  icon,
  secondIcon,
  forceSmallIcon = false,
  tooltipText,
  id,
  popoverContent,
  extraClassNames,
  xstyle,
  contentXstyle,
  testId,
  tooltipTestId,
  tooltipOptions,
  isActive,
  isHighlighted = false,
  badge,
  isHtmlTooltip = false,
  hasArrow = false,
  disabled = false,
  isPrimaryAnSvg = false,
  primaryIconExtraClassNames,
  primaryIconXstyle,
  iconHeight = 24,
  iconWidth = 24,
  primaryIconViewbox = '0 0 24 24',
  popoverAlign = 'start',
  onClick,
  onHide,
}: ToolbarButtonProps) => {
  let tooltipData: QTipDataAttributes | undefined;

  if (tooltipText) {
    tooltipData = {
      'data-qtip-text': tooltipText,
      'data-qtip-placement': tooltipOptions?.position,
      'data-qtip-is-html': isHtmlTooltip,
      'data-qtip-testid': tooltipTestId,
      'data-qtip-delay': tooltipOptions?.delay ?? DEFAULT_TOOL_TIP_DELAY,
    };
  }

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onHide && onHide();
    }
  };

  const triggerProps = stylex.props(styles.trigger);
  const contentProps = stylex.props(
    styles.content,
    isActive ? styles.active : isHighlighted ? styles.highlighted : styles.inactive,
    isSmall ? styles.small : styles.regular,
    isHighlighted && (isSmall ? styles.highlightedSmall : styles.highlightedRegular),
    disabled && styles.disabled,
    xstyle,
  );
  const primaryIconProps = stylex.props(styles.icon, primaryIconXstyle);
  return (
    <PopoverPrimitive.Root defaultOpen={false} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger
        id={id}
        {...triggerProps}
        className={clsx(triggerProps.className, { active: isActive })}
        aria-pressed={isActive}
        disabled={disabled}
        data-testid={testId}
        onClick={(e) => {
          onClick && onClick(e);
        }}>
        <div
          {...tooltipData}
          data-highlighted={isHighlighted || undefined}
          {...contentProps}
          className={clsx(contentProps.className, { active: isActive }, extraClassNames)}>
          <span {...stylex.props(styles.icons)}>
            {isPrimaryAnSvg && typeof icon === 'string' ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                {...primaryIconProps}
                className={clsx(primaryIconProps.className, primaryIconExtraClassNames)}
                viewBox={primaryIconViewbox}
                height={`${iconHeight}px`}
                width={`${iconWidth}px`}
                fill="currentColor"
                data-testid={testId}>
                <path d={getSvgIconPath(icon)} />
              </svg>
            ) : (
              <Icon
                icon={icon}
                testId="firstIcon"
                type="text"
                size={!isSmall && !label && !secondIcon && !forceSmallIcon ? 'lg' : 'sm'}
                extraClassNames={primaryIconExtraClassNames}
                xstyle={[styles.icon, primaryIconXstyle]}
              />
            )}

            {secondIcon && <Icon icon={secondIcon} type="text" testId="secondIcon" />}
            {popoverContent ? (
              <Icon icon={fcArrowDropdown} xstyle={[styles.icon, styles.arrowIcon]} type="text" size="sm" />
            ) : undefined}
          </span>
          {!isSmall && <small {...stylex.props(styles.label)}>{label}</small>}
          {badge ? (
            <span {...stylex.props(styles.badgeSlot)}>
              <Badge testId={testId ? `${testId}-badge` : undefined} xstyle={styles.badge}>
                {badge}
              </Badge>
            </span>
          ) : undefined}
        </div>
      </PopoverPrimitive.Trigger>
      {popoverContent ? (
        <PopoverContent sideOffset={2} align={popoverAlign}>
          <div {...stylex.props(overlayStyles.panel, styles.popover, contentXstyle)}>
            {hasArrow && (
              <PopoverPrimitive.Arrow asChild>
                <div {...stylex.props(overlayStyles.arrow)} />
              </PopoverPrimitive.Arrow>
            )}
            {popoverContent}
          </div>
        </PopoverContent>
      ) : undefined}
    </PopoverPrimitive.Root>
  );
};
